import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ErrorMessageResponse } from '@app/core/models/data-response';
import { DataTaskService } from '@app/core/services/data-task.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateEditDialogComponent, TranslateEditDialogData } from '@app/modal/translate-edit-dialog/translate-edit-dialog.component';
import { debounceTime, from, Subject, switchMap, takeUntil, tap } from 'rxjs';

@Component({
    selector: 'tt-cust-grp2',
    templateUrl: './cust-grp2.component.html',
    styleUrls: ['./cust-grp2.component.css'],
})
export class CustGrp2Component implements OnChanges, OnDestroy {
    @Input()
    public params?: GetCustGrp2Params;

    public get?: CustGrp2Get;

    public saving = false;

    public deleting = false;

    private load$ = new Subject<GetCustGrp2Params>();

    private destroy$ = new Subject<void>();

    @Output()
    public submit = new EventEmitter<SubmitCustGrp2Event>();

    constructor(
        private datatask: DataTaskService,
        private modal: ModalService,
        private dialog: MatDialog,
    ) {
        this.load$
            .pipe(
                takeUntil(this.destroy$),
                tap(() => delete this.get),
                debounceTime(50),
                switchMap((params) => from(this.getCustGrp2(params))),
            )
            .subscribe({
                next: (get) => {
                    this.get = get;
                },
            });
    }

    public async openTranslateDialog() {
        if (!this.get) return;

        const edits = await this.getCustGrp2Langs(this.get);

        const dialogRef = this.dialog.open(TranslateEditDialogComponent, {
            width: '120rem',
            maxWidth: '95vw',
            height: '80vh',
            data: <TranslateEditDialogData<CustGrp2LangEdit>>{
                heading: `(${this.get.custgrp2_keyno}) ${this.get.custgrp2_name}`,
                textToTranslate: this.get.custgrp2_name,
                edits: edits,
                save: async (data) => {
                    await this.datatask.Post(3985, { edits: data.edits });
                    this.get = await this.getCustGrp2(this.params!);
                    dialogRef.close();
                    this.submit.emit({ event: 'save', custgrp2_keyno: this.get!.custgrp2_keyno });
                },
                wordNameKey: 'custgrp2_name',
                wordIdKey: 'custgrp2_keyno',
            },
        });
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            const { errorcode, errormessage, custgrp2_keyno } = await this.saveCustGrp2(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ event: 'save', custgrp2_keyno: custgrp2_keyno });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async deleteValues() {
        if (!this.get) return;

        try {
            this.deleting = true;

            const { errorcode, errormessage } = await this.deleteCustGrp2(this.get);

            if ('' + errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: errormessage });
            } else {
                this.submit.emit({ event: 'delete', custgrp2_keyno: this.get.custgrp2_keyno });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    private async saveCustGrp2(params: CustGrp2Get): Promise<SaveCustGrp2Response> {
        return (await this.datatask.Post(3980, params))[0];
    }

    private async deleteCustGrp2(params: CustGrp2Get): Promise<ErrorMessageResponse> {
        return (await this.datatask.Post(3981, params))[0];
    }

    private async getCustGrp2(params: GetCustGrp2Params): Promise<CustGrp2Get> {
        return (await this.datatask.Post(3979, params))[0];
    }

    private async getCustGrp2Langs(params: CustGrp2Get): Promise<CustGrp2LangEdit[]> {
        return this.datatask.Post(3984, params);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.load$.next(changes['params'].currentValue);
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.load$.complete();
    }
}

export interface CustGrp2Get {
    custgrp2_keyno: string;
    custgrp2_name: string;
    webpage_heading: string;
}

export interface GetCustGrp2Params {
    custgrp2_keyno: string;
}

export interface SaveCustGrp2Response extends ErrorMessageResponse {
    custgrp2_keyno: string;
}

export interface SubmitCustGrp2Event {
    event: 'save' | 'delete';
    custgrp2_keyno: string;
}

interface CustGrp2LangEdit extends Record<string, string> {
    custgrp2_keyno: string;
    custgrp2_name: string;
    language_id: string;
    language_name: string;
    orderby: string;
    google_language_id: string;
}
